import { useCallback, useMemo, useRef, useEffect } from "react";
import { useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import type { ReviewComment, ReviewCommentAuthor, CachedCodeLine } from "@/components/ai/review/types";
import { createReviewComment } from "@/components/ai/review/types";

const STORAGE_KEY_PREFIX = "review-comments";

/**
 * Atom family for storing review comments per environment.
 * Uses localStorage for persistence across sessions.
 */
const reviewCommentsAtomFamily = atomFamily((environmentId: string) =>
    atomWithStorage<ReviewComment[]>(`${STORAGE_KEY_PREFIX}-${environmentId}`, []),
);

/**
 * Atom for tracking whether the review tab should be visible.
 * This is derived from whether there are any comments.
 */
const reviewTabDismissedAtomFamily = atomFamily((environmentId: string) =>
    atomWithStorage<boolean>(`${STORAGE_KEY_PREFIX}-dismissed-${environmentId}`, false),
);

/**
 * Atom family for storing dismissed annotation IDs per environment.
 * When an agent annotation is removed, its ID is stored here to prevent re-adding.
 */
const dismissedAnnotationsAtomFamily = atomFamily((environmentId: string) =>
    atomWithStorage<string[]>(`${STORAGE_KEY_PREFIX}-dismissed-annotations-${environmentId}`, []),
);

/**
 * Hook for managing review comments for a specific environment.
 */
export const useReviewComments = (environmentId: string) => {
    const [comments, setComments] = useAtom(reviewCommentsAtomFamily(environmentId));
    const [isDismissed, setIsDismissed] = useAtom(reviewTabDismissedAtomFamily(environmentId));
    const [dismissedAnnotations, setDismissedAnnotations] = useAtom(dismissedAnnotationsAtomFamily(environmentId));

    // Ref to track dismissed annotations synchronously (avoids race conditions)
    const dismissedAnnotationsRef = useRef<Set<string>>(new Set(dismissedAnnotations));
    useEffect(() => {
        dismissedAnnotationsRef.current = new Set(dismissedAnnotations);
    }, [dismissedAnnotations]);

    const addComment = useCallback(
        (
            filePath: string,
            startLine: number,
            endLine: number,
            content: string,
            author: ReviewCommentAuthor = "user",
            annotationId?: string,
        ) => {
            const comment = createReviewComment(filePath, startLine, endLine, content, author, annotationId);
            setComments((prev) => [...prev, comment]);
            // When a comment is added, show the review tab
            setIsDismissed(false);
            return comment;
        },
        [setComments, setIsDismissed],
    );

    const updateComment = useCallback(
        (commentId: string, content: string) => {
            setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, content, updatedAt: Date.now() } : c)));
        },
        [setComments],
    );

    const removeComment = useCallback(
        (commentId: string) => {
            // Find the comment first to check if it's an agent comment with an annotation ID
            const comment = comments.find((c) => c.id === commentId);
            if (comment && comment.author === "agent" && comment.annotationId) {
                // Update ref immediately to prevent race condition
                dismissedAnnotationsRef.current.add(comment.annotationId);
                // Then persist to storage
                setDismissedAnnotations((prevDismissed) =>
                    prevDismissed.includes(comment.annotationId!)
                        ? prevDismissed
                        : [...prevDismissed, comment.annotationId!],
                );
            }
            setComments((prev) => prev.filter((c) => c.id !== commentId));
        },
        [comments, setComments, setDismissedAnnotations],
    );

    const isAnnotationDismissed = useCallback(
        (annotationId: string): boolean => {
            // Check ref for immediate updates (avoids race condition)
            return dismissedAnnotationsRef.current.has(annotationId);
        },
        [], // No deps needed - ref is always current
    );

    const clearAllComments = useCallback(() => {
        // Mark all agent comments as dismissed before clearing
        // This prevents them from being re-added when the conversation is re-read
        const agentAnnotationIds = comments
            .filter((c) => c.author === "agent" && c.annotationId)
            .map((c) => c.annotationId!);
        if (agentAnnotationIds.length > 0) {
            // Update ref immediately
            for (const id of agentAnnotationIds) {
                dismissedAnnotationsRef.current.add(id);
            }
            // Persist to storage
            setDismissedAnnotations((prevDismissed) => {
                const uniqueIds = agentAnnotationIds.filter((id) => !prevDismissed.includes(id));
                return uniqueIds.length > 0 ? [...prevDismissed, ...uniqueIds] : prevDismissed;
            });
        }
        setComments([]);
    }, [comments, setComments, setDismissedAnnotations]);

    const updateCodeContext = useCallback(
        (commentId: string, contentHash: string, cachedCodeLines: CachedCodeLine[]) => {
            setComments((prev) =>
                prev.map((c) => (c.id === commentId && !c.contentHash ? { ...c, contentHash, cachedCodeLines } : c)),
            );
        },
        [setComments],
    );

    const dismissReviewTab = useCallback(() => {
        setIsDismissed(true);
    }, [setIsDismissed]);

    const showReviewTab = useCallback(() => {
        setIsDismissed(false);
    }, [setIsDismissed]);

    const hasComments = comments.length > 0;
    const isReviewTabVisible = hasComments && !isDismissed;

    const commentsByFile = useMemo(() => {
        const grouped = new Map<string, ReviewComment[]>();
        for (const comment of comments) {
            const existing = grouped.get(comment.filePath) || [];
            existing.push(comment);
            grouped.set(comment.filePath, existing);
        }
        return grouped;
    }, [comments]);

    const getCommentsForFile = useCallback(
        (filePath: string) => {
            return comments.filter((c) => c.filePath === filePath);
        },
        [comments],
    );

    const getCommentsForLineRange = useCallback(
        (filePath: string, startLine: number, endLine: number) => {
            return comments.filter(
                (c) =>
                    c.filePath === filePath &&
                    // Check if the comment's line range overlaps with the given range
                    c.startLine <= endLine &&
                    c.endLine >= startLine,
            );
        },
        [comments],
    );

    return {
        comments,
        commentsByFile,
        hasComments,
        isReviewTabVisible,
        addComment,
        updateComment,
        removeComment,
        clearAllComments,
        dismissReviewTab,
        showReviewTab,
        getCommentsForFile,
        getCommentsForLineRange,
        isAnnotationDismissed,
        updateCodeContext,
    };
};

/**
 * Formats review comments into a message suitable for sending to the agent.
 */
export const formatReviewCommentsForAgent = (comments: ReviewComment[], additionalContext?: string): string => {
    if (comments.length === 0) {
        return "";
    }

    const lines = ["Please address the following review feedback:\n"];

    for (const comment of comments.sort((a, b) => a.filePath.localeCompare(b.filePath) || a.startLine - b.startLine)) {
        const lineRange =
            comment.startLine === comment.endLine
                ? `line ${comment.startLine}`
                : `lines ${comment.startLine} to ${comment.endLine}`;
        lines.push(`### ${comment.filePath} (${lineRange})`);
        lines.push(comment.content);
        lines.push("\n");
    }

    if (additionalContext) {
        lines.push("---\n");
        lines.push("Additional context:");
        lines.push(additionalContext);
    }

    return lines.join("\n");
};
